/**
 * The pending state of a withheld first try (pedagogy spec §5.3.1, §5.3.6): "Inte rätt än", what
 * the wrong value says (the authored diagnosis, else the near-miss kind as a cue, else one generic
 * line), the hint ladder, the retry inputs, and the two ways on — "Kontrollera igen" (one more try)
 * or "Jag kör fast – visa lösningen". No solution, no expected value and no direction is shown.
 *
 * The heading takes focus on arrival and the diagnosis sits in a `role="status"` that the heading
 * describes, so a screen reader hears "Inte rätt än" together with what is wrong. The ladder and
 * the inputs are passed in (`hintLadder`, `children`): the page owns their state.
 *
 * The self-explanation field (`SelfExplanation.tsx`) plugs into `selfExplanation`, between the inputs
 * and the buttons; the slot renders nothing when the page passes none.
 */
import { useEffect, useId, useRef, type ReactNode } from "react";
import { parseNumericAnswer } from "@shared/grading";
import type { ConfidenceLevel } from "@shared/attemptAnswer";
import type { Localized as LocalizedValue } from "@shared/types";
import { Localized } from "@client/components/Localized";
import { Button, Card } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatUnit } from "@client/lib/units";
import { DiagnosisMessage, hasCue } from "./DiagnosisMessage";
import type { PendingPartFeedback } from "./types";

export interface RetryPanelProps {
  /** `numeric` words the wrong parts; `mc` words the wrong pick. */
  kind: "numeric" | "mc";
  /** The first try's parts (numeric); the right ones are kept, the wrong ones are worded here. */
  parts: readonly PendingPartFeedback[];
  /** What was typed for each part the first time, echoed in the diagnosis row. */
  numericRaw?: Record<string, string> | undefined;
  /** The rating the first try was given for a part (the item's single rating when not segmented). */
  confidenceOf: (partId: string) => ConfidenceLevel | undefined;
  /** MC: the authored reason the picked option is wrong. */
  mcDiagnosis?: { message: LocalizedValue } | undefined;
  /** The pending response opened the next hint (the ladder shows it). */
  autoHint?: boolean;
  /** The hint ladder, shown under the diagnosis. */
  hintLadder?: ReactNode;
  /** The retry inputs. */
  children?: ReactNode;
  /** The optional self-explanation field, between the inputs and the buttons. */
  selfExplanation?: ReactNode;
  onSubmit: () => void;
  onStuck: () => void;
  /** The retry request is in flight. */
  submitting?: boolean;
  /** The reveal request is in flight. */
  revealing?: boolean;
  /** Nothing new to check yet (an MC retry before a second pick). */
  submitDisabled?: boolean;
}

/** The typed value with its unit: as typed when it names one, else the part's own unit. */
function typedLabel(raw: string, unit: string): string {
  const value = raw.trim();
  const parsed = parseNumericAnswer(value);
  const namesUnit = "value" in parsed && parsed.unit !== undefined;
  return namesUnit || !unit ? value : `${value} ${formatUnit(unit)}`;
}

export function RetryPanel({
  kind,
  parts,
  numericRaw,
  confidenceOf,
  mcDiagnosis,
  autoHint = false,
  hintLadder,
  children,
  selfExplanation,
  onSubmit,
  onStuck,
  submitting,
  revealing,
  submitDisabled,
}: RetryPanelProps) {
  const t = useT();
  const { lang } = useLang();
  const headlineRef = useRef<HTMLSpanElement>(null);
  const statusId = useId();
  const retryHeadingId = useId();

  // The page mounts this panel the moment the withheld answer arrives, and the button that had
  // focus unmounts with the answer area: focus the heading so the state is announced.
  useEffect(() => {
    headlineRef.current?.focus();
  }, []);

  const wrongParts = parts.filter((part) => !part.correct);
  const multiPart = parts.length > 1;
  const anyCue = wrongParts.some(hasCue);

  /** "Ditt svar 0,0476 nm · du var ganska säker" (or "Ditt svar: …" without a rating). */
  function yourAnswer(part: PendingPartFeedback): string | null {
    const raw = numericRaw?.[part.id];
    if (raw === undefined || raw.trim() === "") return null;
    const value = typedLabel(raw, part.unit);
    const rating = confidenceOf(part.id);
    return rating === undefined
      ? `${t("practice.feedback.yourAnswer")} ${value}`
      : t("practice.feedback.yourAnswerConfidence", {
          value,
          level: t(`common.confidence.${rating}`).toLocaleLowerCase(lang),
        });
  }

  return (
    <Card
      className="mt-4"
      title={
        <span
          ref={headlineRef}
          tabIndex={-1}
          aria-describedby={statusId}
          className="inline-flex items-center gap-2 outline-none"
        >
          {t("practice.feedback.notYet")}
        </span>
      }
    >
      <div id={statusId} role="status" className="space-y-2 text-sm text-ink-800 dark:text-ink-100">
        {kind === "mc" ? (
          mcDiagnosis ? (
            <Localized value={mcDiagnosis.message} variant="inline" />
          ) : (
            <p>{t("practice.diagnosis.genericMc")}</p>
          )
        ) : (
          <>
            {wrongParts.map((part) => {
              const answer = yourAnswer(part);
              if (!answer && !hasCue(part)) return null;
              return (
                <div key={part.id}>
                  {multiPart && <strong>({part.id}) </strong>}
                  {answer && <span className="text-ink-600 dark:text-ink-400">{answer}</span>}
                  {hasCue(part) && (
                    <DiagnosisMessage
                      diagnosis={part.diagnosis}
                      nearMiss={part.nearMiss}
                      unit={part.unit}
                      pending
                      className="mt-0.5 block font-medium text-amber-800 dark:text-amber-300"
                    />
                  )}
                </div>
              );
            })}
            {!anyCue && <p>{t("practice.diagnosis.genericNumeric")}</p>}
          </>
        )}
        {autoHint && (
          <p className="text-ink-600 dark:text-ink-400">{t("practice.retry.autoHint")}</p>
        )}
      </div>

      {hintLadder}

      <section aria-labelledby={retryHeadingId} className="mt-4">
        <h3 id={retryHeadingId} className="text-sm font-semibold text-ink-900 dark:text-ink-50">
          {t("practice.retry.title")}
        </h3>
        <p className="mb-3 mt-1 text-sm text-ink-600 dark:text-ink-400">
          {t("practice.retry.caption")}
        </p>
        {children}
      </section>

      {selfExplanation}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          className="max-sm:min-h-11"
          onClick={onSubmit}
          loading={submitting}
          disabled={submitDisabled}
        >
          {t("practice.retry.submit")}
        </Button>
        <Button
          className="max-sm:min-h-11"
          variant="secondary"
          onClick={onStuck}
          loading={revealing}
        >
          {t("practice.retry.stuck")}
        </Button>
      </div>
      <p className="mt-2 text-xs text-ink-600 dark:text-ink-400">
        {t("practice.feedback.solutionLocked")}
      </p>
    </Card>
  );
}
